import { useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { IntlProvider } from "react-intl";
import Cookies from "js-cookie";
import { toast, ToastContainer } from "react-toastify";
import jwt_decode from "jwt-decode";
import moment from "moment";
import PropTypes from "prop-types";
import "moment/locale/de";
import "moment/locale/es";
import "moment/locale/fr";
import "moment/locale/zh-cn";
import "moment/locale/ko";
import "moment/locale/ja";
import "moment/locale/pt";
import "moment/locale/th";

import "react-toastify/dist/ReactToastify.css";
import "./sass/App.scss";
import Main from "components/atoms/Main/Main";
import Navigation from "components/molecules/Navigation/Navigation";
import { getRequest, postRequest } from "api/axiosClient";
import TermsAndConditions from "components/molecules/TermsAndConditions/TermsAndConditions";
import useToken from "hooks/useToken";
import usePermissions from "hooks/usePermissions";
import locale_en from "locales/en.json";
import locale_de from "locales/de.json";
import locale_es from "locales/es.json";
import locale_pt from "locales/pt.json";
import locale_kr from "locales/kr.json";
import locale_ja from "locales/ja.json";
import locale_fr from "locales/fr.json";
import locale_zh_cn from "locales/zh-cn.json";
import locale_th from "locales/th.json";
import errorToast from "helpers/errorToast";
import LoggedInAsBanner from "components/molecules/LoggedInAsBanner/LoggedInAsBanner";
import TimezoneBanner from "components/molecules/TimezoneBanner/TimezoneBanner";
import SplashLoader from "components/atoms/SplashLoader/SplashLoader";
import NavProvider from "components/molecules/Navigation/components/common/NavProvider";
import { identifyPosthogUser, initPosthog } from "helpers/posthog";
import ErrorBoundary from "ErrorBoundary";
import ErrorPage from "pages/common/ErrorPage/ErrorPage";
import { acceptLegalAgreements, getBookmarks, getUserCustomSettings } from "api";
import {
  mustAcceptLegalAgreements,
  syncLegacyAcceptanceIfNeeded,
} from "helpers/legalAgreements";
import { unsubscribeFromPushManager } from "helpers/notificationHelpers";
import Router from "router/Router";
import { clearFiltersFromLocalStorage } from "helpers/localStorage";
import { resolveAppLocale } from "helpers/localeHelpers";
import useHubspotChat from "hooks/useHubspotChat";
import useIntercomChat from "hooks/useIntercomChat";
import useSupportChatProvider from "hooks/useSupportChatProvider";
import { resolveIntercomUser } from "helpers/resolveIntercomUser";
import { useRouteHistory } from "hooks/useRouteHistory";
import fullscreenRoutes from "constants/FullscreenRoutes";
import { BottomBannersProvider } from "context/BottomBannersContext";
import { PublicRoutes } from "./constants";
import ScrollToTop from "./helpers/scrollToTop";
import ActivateAccount from "./pages/shipper/Settings/components/OnBoardingForm/OnBoardingForm";

function formatDateStringForChinese(formatStr) {
  return formatStr
    .replace(/DD/g, "DD日")
    .replace(/(?<!M)MM(?!M)/g, "MMM")
    .replace(/MMMM/g, "MMM")
    .replace(/DD日 MMM/g, "MMM DD日");
}

const messages = {
  en: locale_en,
  de: locale_de,
  es: locale_es,
  fr: locale_fr,
  kr: locale_kr,
  ja: locale_ja,
  th: locale_th,
  "pt-br": locale_pt,
  "zh-cn": locale_zh_cn,
};

// Stable no-op so NavProvider's required `handleNavigation` prop has a permanent reference.
// The expand/collapse state is read directly from NavContext where it's needed (see Main),
// which avoids re-rendering the whole App tree on every sidebar toggle.
const noop = () => {};

function BottomBanners({ children }) {
  return <div className="bottom-banners">{children}</div>;
}

BottomBanners.propTypes = {
  children: PropTypes.node.isRequired,
};

const App = function () {
  const { isIntercomEnabled, isHubspotEnabled } = useSupportChatProvider();
  const [intercomUserJwt, setIntercomUserJwt] = useState(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const isHubspotChatLoaded = useHubspotChat(isHubspotEnabled);
  useIntercomChat({ enabled: isIntercomEnabled, intercomUserJwt });
  const { user, logout, getIdTokenClaims } = useAuth0();
  const { setToken, token, clearToken } = useToken();
  const { setPermissions } = usePermissions();
  useRouteHistory();
  const { REACT_APP_CARGOBASE_BASE_URL, REACT_APP_POSTHOG_KEY } = import.meta.env;

  const [currentLocale, setCurrentLocale] = useState(getInitialLocale());
  const [loggedInAsUserName, setLoggedInAsUserName] = useState(null);
  const [isStaff, setIsStaff] = useState(false);
  const [isGuest, setIsGuest] = useState(false);
  const [userType, setUserType] = useState(null);
  const [isLoggedInAs, setIsLoggedInAs] = useState(Cookies.get("cargobase_logged_in_as_token") !== undefined);
  const [firstTimeAppLoaded, setFirstTimeAppLoaded] = useState(false);
  const [timezoneBannerShown, setTimezoneBannerShown] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isloading, setIsLoading] = useState(null);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [showLegalAgreementsModal, setShowLegalAgreementsModal] = useState(false);
  const location = useLocation();

  const pathToRegex = (path) => new RegExp(`^${path.replace(/:[^\s/]+/g, "([\\w-]+)")}$`);
  const isFullscreenRoute = fullscreenRoutes.some((route) => pathToRegex(route).test(window.location.pathname));
  const isPublicRoute = PublicRoutes.some((route) => route === location.pathname);

  useEffect(() => {
    if (window.location.pathname !== "/") {
      setFirstTimeAppLoaded(true);
    }
    window.addEventListener("online", updateStatus);
    window.addEventListener("offline", updateStatus);
  }, []);

  // Get Cargobase API token from Auth0 user_metadata
  useEffect(() => {
    const getUserMetadata = async () => {
      if (user) Sentry.setUser({ email: user.email });
      if (user && !Cookies.get("cargobase_usertoken")) {
        try {
          setIsLoading(true);
          const claims = await getIdTokenClaims();
          // eslint-disable-next-line no-underscore-dangle
          const idToken = claims.__raw;
          Cookies.set("auth0_id_token", idToken, { expires: 7 });
          await postRequest(`${REACT_APP_CARGOBASE_BASE_URL}/user/token`)
            .then((res) => {
              // Replace auth0 token with Cargobase API token
              const result = res.data;
              Cookies.remove("auth0_id_token");

              Cookies.set("cargobase_user_refresh_token", result.refresh);
              // Check if user is staff
              const decoded = jwt_decode(result.access);
              setIsStaff(decoded.is_staff);
              setToken(result.access);
              if (decoded.is_staff && !isLoggedInAs) {
                setIsLoading(false);
              }
              if (res?.status === 200) {
                setIsError(false);
              }
            })
            .then(() => {
              if (Cookies.get("is_guest") !== "true") {
                getUserCustomSettings().then((res) => {
                  localStorage.setItem("customSettings", JSON.stringify(res.data));
                });
              }
            })
            .catch((err) => {
              errorToast(err);
              if (err?.response?.status !== 200) {
                setIsError(true);
              }
              setIsLoading(false);
            });
        } catch (err) {
          errorToast(err, "Could not retrieve user token from Auth0.");
        }
      }
    };
    getUserMetadata();
  }, [user?.sub]);

  /**
   * Get user permissions
   *
   * This useEffect runs a getPermissions() async function which retrieves the user permissions object
   * from the cargobase API every 5 minutes or when the user token changes.
   */
  const syncIntercomJwt = (permissions) => {
    const nextJwt = resolveIntercomUser(permissions)?.intercom_user_jwt ?? null;
    setIntercomUserJwt((prev) => (prev === nextJwt ? prev : nextJwt));
  };

  useEffect(() => {
    if (!isIntercomEnabled) {
      setIntercomUserJwt(null);
      return;
    }

    const storedPermissions = localStorage.getItem("cargobase_userpermissions");
    if (storedPermissions) {
      syncIntercomJwt(JSON.parse(storedPermissions));
    }
  }, [isIntercomEnabled]);

  useEffect(() => {
    if (token) {
      if (!isGuest) {
        getBookmarks()
          .then((res) => localStorage.setItem("bookmarks", JSON.stringify(res.data.bookmarked_items)))
          .catch((err) => {
            errorToast(err);
          });
      }
      getPermissions();
      const interval = setInterval(() => {
        getPermissions();
      }, 300000);
      return () => clearInterval(interval);
    }

    setIntercomUserJwt(null);
    setPermissionsLoaded(false);
    setShowLegalAgreementsModal(false);
  }, [token]);

  moment.fn.format = (function (originalFormat) {
    return function (formatStr) {
      const locale = moment.locale();
      // If the locale is "zh-cn" and the format includes `DD`
      if (locale === "zh-cn" && !formatStr.includes("日") && !formatStr.endsWith("DD")) {
        return originalFormat.call(this, formatDateStringForChinese(formatStr));
      }
      return originalFormat.call(this, formatStr);
    };
  })(moment.fn.format);

  const getPermissions = async () => {
    if (token) {
      // Check if user is a staff or guest user
      const decoded = jwt_decode(token);
      const isStaff = decoded.is_staff;
      const isGuest = decoded.is_guest_user && !decoded.is_staff; // Staff token also has is_guest_user set to true
      setIsStaff(isStaff);
      setIsGuest(isGuest);
      Cookies.set("is_guest", isGuest);
      Sentry.setTag("is_staff", isStaff);
      Sentry.setTag("is_guest", isGuest);

      // Get user permissions if the user is not staff or the user is staff and has a 'logged in as' token
      if (token || isLoggedInAs)
        getRequest(`${REACT_APP_CARGOBASE_BASE_URL}/user/permissions`)
          .then(async (res) => {
            const userPermission = {};
            const userCustomLabel = {};
            Object.keys(res.data).forEach((key) => {
              if (key !== "locations_custom_labels" && key !== "company_custom_labels") userPermission[key] = res.data[key];
              else userCustomLabel[key] = res.data[key];
            });

            await syncLegacyAcceptanceIfNeeded(
              userPermission,
              acceptLegalAgreements,
              async () => {
                const refreshRes = await getRequest(`${REACT_APP_CARGOBASE_BASE_URL}/user/permissions`);
                const refreshed = {};
                Object.keys(refreshRes.data).forEach((key) => {
                  if (key !== "locations_custom_labels" && key !== "company_custom_labels") {
                    refreshed[key] = refreshRes.data[key];
                  }
                });
                Object.assign(userPermission, refreshed);
              },
            );

            setPermissionsLoaded(true);
            setShowLegalAgreementsModal(mustAcceptLegalAgreements(userPermission));

            const {
              full_name,
              company_id,
              company_name,
              preferred_timezone,
              user_id,
              user_role,
              user_type,
              email,
              first_name,
              last_name,
            } = userPermission;
            setUserType(user_type);

            initPosthog({
              apiKey: REACT_APP_POSTHOG_KEY,
            });
            identifyPosthogUser(email, company_id, {
              full_name,
              user_id,
              company_id,
              preferred_timezone,
              user_role,
              user_type,
              email,
              company_name,
            });

            Sentry.setTag("user_id", user_id);
            Sentry.setTag("user_role", user_role);
            Sentry.setTag("user_type", user_type);
            Sentry.setTag("company_id", company_id);
            Sentry.setTag("preferred_timezone", preferred_timezone);
            Sentry.setTag("full_name", full_name);

            setLoggedInAsUserName(full_name);
            localStorage.setItem("cargobase_userpermissions", JSON.stringify(userPermission));
            localStorage.setItem("user_custom_label", JSON.stringify(userCustomLabel));
            setPermissions(JSON.stringify(res.data));
            if (!currentLocale) {
              handleLocaleChange(resolveAppLocale(res.data.language));
            }
            const intercomPayload = resolveIntercomUser(userPermission);
            syncIntercomJwt(userPermission);

            if (import.meta.env.DEV && isIntercomEnabled && !intercomPayload) {
              console.warn(
                "[Intercom] Missing intercom_jwt_token in /user/permissions — Messenger will not boot until the API returns a signed JWT.",
              );
            }

            /* eslint-disable no-underscore-dangle -- HubSpot queue API */
            if (!isIntercomEnabled && !isLoggedInAs && !isStaff && window?._hsq && window._hsq?.push) {
              window._hsq.push([
                "identify",
                {
                  email,
                  firstName: first_name,
                  lastName: last_name,
                  userRole: user_role,
                  userType: user_type,
                  company: company_name,
                },
              ]);
            }
            /* eslint-enable no-underscore-dangle */
          })
          .catch((err) => {
            if (!isStaff) {
              setPermissionsLoaded(false);
              setShowLegalAgreementsModal(false);
              localStorage.removeItem("cargobase_userpermissions");
              clearToken();
              unsubscribeFromPushManager();
              clearFiltersFromLocalStorage();
              logout({
                logoutParams: {
                  returnTo: window.location.origin,
                },
              });
              errorToast(err, "Could not retrieve user permissions.");
            }
          })
          .finally(() => {
            setIsLoading(false);
          });
    }
  };

  const toastId = useRef(null);
  const updateStatus = () => {
    if (navigator.onLine) {
      if (toastId.current) toast.dismiss(toastId.current);
      toast("You're back online", { type: "success" });
    } else {
      toastId.current = toast("You're currently offline", { type: "error", autoClose: false });
    }
  };

  function setGlobalMomentLocale(locale) {
    if (locale === "kr") {
      moment.locale("ko");
    } else {
      moment.locale(locale);
    }
  }

  function getInitialLocale() {
    const savedLocale = localStorage.getItem("locale");
    const setNewLocale = resolveAppLocale(savedLocale);
    if (savedLocale && savedLocale !== setNewLocale) {
      localStorage.setItem("locale", setNewLocale);
    }
    Sentry.setTag("page_locale", setNewLocale);
    setGlobalMomentLocale(setNewLocale);
    return setNewLocale;
  }

  function handleLocaleChange(locale) {
    const resolvedLocale = resolveAppLocale(locale);
    Sentry.setTag("page_locale", resolvedLocale);
    setCurrentLocale(resolvedLocale);
    localStorage.setItem("locale", resolvedLocale);
    setGlobalMomentLocale(resolvedLocale);
  }

  function clearLocale() {
    setCurrentLocale(null);
    localStorage.removeItem("locale");
  }

  function updateAppLoadStatus(status) {
    setFirstTimeAppLoaded(status);
  }

  if (isError && !isloading)
    return (
      <IntlProvider locale={currentLocale} messages={messages[currentLocale]}>
        <div className="h-100 d-flex align-items-center justify-content-center">
          <ErrorPage />
        </div>
      </IntlProvider>
    );

  if (!isError && isloading) return <SplashLoader />;
  if (isStaff && !isLoggedInAs)
    return (
      <IntlProvider locale={currentLocale} messages={messages[currentLocale]}>
        <ErrorBoundary>
          <Router setIsLoading={setIsLoading} setIsLoggedInAs={setIsLoggedInAs} getPermissions={getPermissions} isStaff />
        </ErrorBoundary>
      </IntlProvider>
    );
  return (
    <IntlProvider locale={currentLocale} messages={messages[currentLocale]}>
      {!window.location.href.includes("resetAll") &&
        location.pathname !== "/signup" &&
        location.pathname !== "/guest-signup/verify-activation" &&
        !isPublicRoute && (
          <TermsAndConditions
            show={permissionsLoaded && showLegalAgreementsModal}
            onAccepted={() => setShowLegalAgreementsModal(false)}
            onPermissionsUpdated={(updated) => {
              localStorage.setItem("cargobase_userpermissions", JSON.stringify(updated));
              setPermissions(JSON.stringify(updated));
              setShowLegalAgreementsModal(mustAcceptLegalAgreements(updated));
            }}
          />
        )}
      <ScrollToTop>
        <div className="App">
          <ToastContainer position="bottom-right" autoClose={5000} newestOnTop />

          <NavProvider handleNavigation={noop}>
            {!isPublicRoute &&
              !isFullscreenRoute &&
              location.pathname !== "/signup" &&
              location.pathname !== "/guest-signup/verify-activation" && (
                <Navigation userType={userType} isStaff={isStaff} />
              )}
            {location.pathname === "/signup" || location.pathname === "/guest-signup/verify-activation" ? (
              <ActivateAccount setToken={setToken} setIsLoggedInAs={setIsLoggedInAs} />
            ) : (
              <ErrorBoundary>
                <BottomBannersProvider
                  timezoneBannerShown={timezoneBannerShown}
                  isLoggedInAs={isLoggedInAs}
                  loggedInAsUserName={loggedInAsUserName}
                >
                  <Main banners={[timezoneBannerShown, isLoggedInAs]} isFullscreen={isFullscreenRoute}>
                    {!isPublicRoute && (
                      <BottomBanners>
                        <LoggedInAsBanner
                          loggedInAsUserName={loggedInAsUserName}
                          setIsLoggedInAs={setIsLoggedInAs}
                          isLoggedInAs={isLoggedInAs}
                          isFullscreenRoute={isFullscreenRoute}
                          clearLocale={clearLocale}
                        />
                        <TimezoneBanner
                          setTimezoneBannerShown={setTimezoneBannerShown}
                          isFullscreenRoute={isFullscreenRoute}
                        />
                      </BottomBanners>
                    )}
                    <Router
                      currentLocale={currentLocale}
                      handleLocaleChange={handleLocaleChange}
                      firstTimeAppLoaded={firstTimeAppLoaded}
                      updateAppLoadStatus={updateAppLoadStatus}
                      isGuest={isGuest}
                    />
                  </Main>
                  <div id="fullscreen-routes" />
                </BottomBannersProvider>
              </ErrorBoundary>
            )}
          </NavProvider>
        </div>
      </ScrollToTop>
    </IntlProvider>
  );
};

export default App;
